import { useMemo, type ReactNode } from "react";
import { countryDisplayName, countryOptions, isCountryCode } from "@app/core/countries";
import { useLocale } from "../locale/LocaleProvider.js";
import { Select, type SelectOption } from "./Select.js";

export interface CountrySelectProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Show a clear (×) affordance — use for optional country fields. */
  allowClear?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  id?: string;
}

function optionLabel(flag: string, name: string): ReactNode {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span aria-hidden style={{ fontSize: "1.15em", lineHeight: 1 }}>
        {flag}
      </span>
      <span>{name}</span>
    </span>
  );
}

/**
 * Searchable, flag-annotated country picker. Value in/out is the ISO 3166-1
 * alpha-2 code (what the backend stores); the flag and localized name are
 * presentation only. Search matches the localized name, the English and Turkish
 * names, and the code — so a Turkish user can still find "Germany" by any of
 * those. A value that isn't a known code (legacy free-text) is shown verbatim.
 */
export function CountrySelect(props: CountrySelectProps) {
  const { locale } = useLocale();
  const { value } = props;

  const options = useMemo<SelectOption[]>(() => {
    const opts: SelectOption[] = countryOptions(locale).map(({ code, name, flag }) => ({
      value: code,
      label: optionLabel(flag, name),
      // Cross-locale haystack: current-locale name + EN + TR names + the code.
      searchText: `${name} ${countryDisplayName(code, "en")} ${countryDisplayName(code, "tr")} ${code}`,
    }));
    // Keep an unrecognized existing value selectable so legacy entries render.
    if (value && !isCountryCode(value)) {
      opts.unshift({ value, label: value, searchText: value });
    }
    return opts;
  }, [locale, value]);

  return (
    <Select
      options={options}
      showSearch
      {...(props.value !== undefined ? { value: props.value } : {})}
      {...(props.onChange ? { onChange: props.onChange } : {})}
      {...(props.label !== undefined ? { label: props.label } : {})}
      {...(props.help !== undefined ? { help: props.help } : {})}
      {...(props.error !== undefined ? { error: props.error } : {})}
      {...(props.required !== undefined ? { required: props.required } : {})}
      {...(props.disabled !== undefined ? { disabled: props.disabled } : {})}
      {...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {})}
      {...(props.allowClear !== undefined ? { allowClear: props.allowClear } : {})}
      {...(props.size !== undefined ? { size: props.size } : {})}
      {...(props.className !== undefined ? { className: props.className } : {})}
      {...(props.id !== undefined ? { id: props.id } : {})}
    />
  );
}
